import type { UseQueryResult } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import type { Catalog, Thread } from "@/entities/thread";
import { DesktopCatalogGrid } from "@/features/catalog/components/desktop";
import { CatalogItem } from "@/features/catalog/components/lists/CatalogItem";
import { MobileCatalogGridLayout } from "@/features/catalog/components/lists/MobileCatalogGridLayout";
import { useFavoriteThreads } from "@/features/catalog/hooks/useFavoriteThreads";
import { useFilteredThreads } from "@/features/catalog/hooks/useFilteredThreads";
import { useOekakiFloor } from "@/features/catalog/hooks/useOekakiFloor";
import {
  type CatalogEvent,
  isCatalogEvent,
} from "@/features/catalog/mediators/catalogEvents";
import { createCatalogGridMediator } from "@/features/catalog/mediators/catalogGridMediator";
import {
  createCatalogContext,
  getLastCatalogIds,
  getNewCatalogThreadIds,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import { CatalogSkeleton } from "@/features/catalog/ui/CatalogSkeleton";
import { catalogContextBoardSlug } from "@/features/catalog/utils/catalogContext";
import { useIsDesktop } from "@/shared/hooks";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyResource,
} from "@/shared/ui/component-hierarchy/hooks";
import { Message } from "@/shared/ui/feedback";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";

interface CatalogGridProps {
  query: UseQueryResult<Catalog>;
  onDesktopRefresh: () => void;
  wheelReloadEnabled?: boolean;
}

export const CatalogGrid: React.FunctionComponent<CatalogGridProps> = ({
  query,
  onDesktopRefresh,
  wheelReloadEnabled = true,
}: CatalogGridProps) => {
  const isDesktop = useIsDesktop();
  const effects = useRef({
    retry: query.refetch,
    refresh: onDesktopRefresh,
  });
  effects.current = { retry: query.refetch, refresh: onDesktopRefresh };
  const mediator = useHierarchyResource((_previous) =>
    createCatalogGridMediator((event) => {
      if (event.type === "catalog/retry-requested") {
        void effects.current.retry();
      } else {
        effects.current.refresh();
      }
    }),
  );
  const node = useComponentNode((event) => {
    if (
      !isCatalogEvent(event) ||
      (event.type !== "catalog/retry-requested" &&
        event.type !== "catalog/refresh-requested")
    ) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  return (
    <ComponentScope node={node}>
      {isDesktop ? (
        <DesktopCatalogGrid
          query={query}
          wheelReloadEnabled={wheelReloadEnabled}
        />
      ) : (
        <MobileCatalogGrid query={query} />
      )}
    </ComponentScope>
  );
};

interface MobileCatalogGridProps {
  query: UseQueryResult<Catalog>;
}

const MobileCatalogGrid: React.FunctionComponent<MobileCatalogGridProps> = ({
  query,
}: MobileCatalogGridProps) => {
  const { data, error, isFetching, isLoading } = query;
  const node = useComponentNode();
  const {
    columns,
    rows,
    imageSize,
    textLines,
    catalogFontScale,
    currentSort,
    lastCatalogIds,
  } = useCatalogStore();

  const filteredThreads = useFilteredThreads(data?.threads);
  // そうだね閾値を満たすお絵描きスレを中間ラインより下に沈ませない(お気に入りは最上位を維持するため後段で適用)
  const flooredThreads = useOekakiFloor(filteredThreads);
  const visibleThreads = useFavoriteThreads(flooredThreads);

  const newThreadIds = useMemo(() => {
    const context = createCatalogContext(
      catalogContextBoardSlug(),
      // SP版は取得ページを末尾へ統合するため、履歴も先頭ページの文脈へ集約する。
      1,
      currentSort,
    );
    return getNewCatalogThreadIds(
      data?.threads.map((thread) => thread.id) ?? [],
      getLastCatalogIds({ lastCatalogIds }, context),
    );
  }, [currentSort, data?.threads, lastCatalogIds]);

  return (
    <ComponentScope node={node}>
      <MobileCatalogGridView
        error={error}
        hasData={data !== undefined}
        isLoading={isLoading}
        isRetrying={isFetching}
        sourceThreadCount={data?.threads.length ?? 0}
        visibleThreads={visibleThreads}
        newThreadIds={newThreadIds}
        columns={columns}
        rows={rows}
        imageSize={imageSize}
        textLines={textLines}
        fontScale={catalogFontScale}
        onEvent={(event: CatalogEvent): void => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
};

export interface MobileCatalogGridViewProps {
  readonly error: Error | null;
  readonly hasData: boolean;
  readonly isLoading: boolean;
  readonly isRetrying: boolean;
  readonly sourceThreadCount: number;
  readonly visibleThreads: readonly Thread[];
  readonly newThreadIds: ReadonlySet<string>;
  readonly columns: number;
  readonly rows: number;
  readonly imageSize: number;
  readonly textLines: number;
  readonly fontScale: number;
  readonly onEvent: (event: CatalogEvent) => void;
}

export function MobileCatalogGridView({
  error,
  hasData,
  isLoading,
  isRetrying,
  sourceThreadCount,
  visibleThreads,
  newThreadIds,
  columns,
  rows,
  imageSize,
  textLines,
  fontScale,
  onEvent,
}: MobileCatalogGridViewProps): React.ReactElement {
  return (
    <QueryStateBoundary
      error={error}
      hasData={hasData}
      isLoading={isLoading}
      isRetrying={isRetrying}
      message="スレッド一覧の読み込みに失敗しました"
      onRetry={(): void => onEvent({ type: "catalog/retry-requested" })}
      loadingFallback={
        <CatalogSkeleton
          columns={columns}
          rows={rows}
          imageSize={imageSize}
          textLines={textLines}
          fontScale={fontScale}
          variant="mobile"
        />
      }
    >
      {visibleThreads.length === 0 ? (
        <Message variant="info">
          {sourceThreadCount > 0
            ? "検索条件に一致するスレッドが見つかりません"
            : "スレッドが見つかりません"}
        </Message>
      ) : (
        <MobileCatalogGridLayout columns={columns} reserveFooterSpace={false}>
          {visibleThreads.map((thread) => (
            <CatalogItem
              key={thread.id}
              thread={thread}
              isNew={newThreadIds.has(thread.id)}
            />
          ))}
        </MobileCatalogGridLayout>
      )}
    </QueryStateBoundary>
  );
}
